import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData, getTablebySql } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, useMenuStore, globalMenu } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import { showPopupModal } from "@widgets/CommonModal";
import SN_MAS_BAS_REG_001_TEX_POP_001 from "@menu/XTC/SN_MAS_BAS_REG_001_TEX/SN_MAS_BAS_REG_001_TEX_POP_001";
import SN_MAS_BAS_REG_001_TEX_POP_002 from "@menu/XTC/SN_MAS_BAS_REG_001_TEX/SN_MAS_BAS_REG_001_TEX_POP_002";

interface Props {
  Menuprops: menuInfo;
}

const SN_MAS_BAS_REG_001_TEX = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const { menuItems } = useMenuStore();

  const searchRef = useRef<any>(null);
  const sheetref1 = useRef<any>(null); // excel download 용
  const baseSheetColsRef = useRef<any[]>([]); // sheet1 정적 컬럼(자식 flag 컬럼을 뒤에 붙이는 기준)
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  // sheet1 SELECT가 반환하는 기본/시스템 컬럼. 나머지 키(cd_flag)만 동적 엑셀 컬럼으로 만든다.
  const SHEET_BASE_KEYS = new Set([
    "cd_firm",
    "no_style",
    "nm_style",
    "cd_cust",
    "nm_cust",
    "no_reference",
    "dc_rmk",
    "js_addinfo",
  ]);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelDownLoad,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        baseSheetColsRef.current = rtn.grid.sheet1 ?? [];
        setColums({
          grid1: rtn.grid.grid1,
          sheet1: rtn.grid.sheet1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  // sheet1(엑셀다운로드) 데이터의 cd_flag 키로 동적 컬럼을 생성해 정적 컬럼 뒤에 붙인다.
  // 헤더=cd_flag, 값=1(존재표시). 컬럼 순서는 flagOrder(쿼리 3번째 결과: MAS166→167→168, 그룹 내 no_seq_sort).
  const buildSheetColumns = (rows: any[], flagOrder: string[]) => {
    const baseCols = baseSheetColsRef.current ?? [];

    // 결과 행에 실제 존재하는 flag 키만 수집
    const present = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (SHEET_BASE_KEYS.has(key)) continue;
        present.add(key);
      }
    }

    // flagOrder 순서대로 정렬, 목록에 없는 flag는 뒤에 알파벳순으로 append
    const orderIndex = new Map<string, number>();
    flagOrder.forEach((f, i) => orderIndex.set(f, i));
    const flags = Array.from(present).sort((a, b) => {
      const ia = orderIndex.has(a)
        ? orderIndex.get(a)!
        : Number.MAX_SAFE_INTEGER;
      const ib = orderIndex.has(b)
        ? orderIndex.get(b)!
        : Number.MAX_SAFE_INTEGER;
      return ia - ib || a.localeCompare(b);
    });

    // 헤더/필드 shape을 기존 정적 컬럼과 맞추기 위해 SnTextBox 정적 컬럼을 템플릿으로 복제
    const template =
      baseCols.find((c: any) => c.tp_column === "SnTextBox") ??
      baseCols[0] ??
      {};

    const flagCols = flags.map((flag, i) => ({
      ...template,
      cd_column: flag,
      nm_column: flag,
      tp_column: "SnTextBox",
      cd_displaycolumn: "",
      cd_clas: "",
      js_combo: undefined,
      fg_visible: "Y",
      yn_required: "N",
      yn_sum: "N",
      qt_width: 90,
      no_seq: 1000 + i,
    }));

    return [...baseCols, ...flagCols];
  };

  const onSearch = async () => {
    try {
      const modifiedRows1 = gridRefs.current["grid1"].current.getChange();
      if (modifiedRows1.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const result = await onSave();
          if (!result) return;
        }
      }

      const params = {
        id: "sn_mas_bas_reg_001_tex_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      if (data) {
        const sheetRows = data[1] ?? [];
        // data[2]: 정렬된 flag 목록 (MAS166→167→168, 그룹 내 no_seq_sort)
        const flagOrder = (data[2] ?? []).map((r: any) => r.cd_flag);
        // 자식 cd_flag 컬럼을 조회 결과에 맞춰 동적으로 재구성
        setColums((prev: any) => ({
          ...prev,
          sheet1: buildSheetColumns(sheetRows, flagOrder),
        }));
        setDataSources({
          grid1: data[0],
          sheet1: sheetRows,
        });
      } else {
        setColums((prev: any) => ({
          ...prev,
          sheet1: baseSheetColsRef.current ?? [],
        }));
        setDataSources({
          grid1: [],
          sheet1: [],
        });
      }
    } catch (error: any) {}
  };

  const onInsert = async () => {
    const newRow = {
      cd_firm: userInfo?.cd_firm,
      no_style: "",
      nm_style: "",
      cd_cust: "",
      no_reference: "",
      dc_rmk: "",
      tp_item: "",
      yn_itemcod: "",
    };
    gridRefs.current["grid1"].current?.addRow(newRow);
    // setTimeout(() => {
    gridRefs.current["grid1"].current.setFocusedCell(
      gridRefs.current["grid1"].current.getDisplayedRowCount() - 1,
      "no_style"
    );
    // }, 100);
  };

  const onDelete = async () => {
    try {
      gridRefs.current["grid1"].current.deleteSelectedRows();
    } catch (error) {}
  };

  const onExcelDownLoad = async () => {
    try {
      const displayedCols = sheetref1.current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());
      sheetref1.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();

      const requiredValuesCheck =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheck) return false;

      const gridGetChanges = gridRefs.current["grid1"].current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const allRowData: any[] = [];
      gridRefs.current["grid1"].current.forEachFilteredNode((node: any) => {
        allRowData.push(node.data);
      });

      const seen = new Set<string>();
      for (let i = 0; i < allRowData.length; i++) {
        const row = allRowData[i];
        const No_style = row?.no_style?.trim();
        if (!No_style) continue;

        if (seen.has(No_style)) {
          await showDialog(
            "error",
            `[스타일코드: ${No_style}] 해당 스타일 코드는 이미 등록되어 있습니다. 다른 코드로 변경해 주세요.`
          );
          return false;
        }

        seen.add(No_style);
      }

      const addedRows = gridGetChanges?.insert || [];
      if (addedRows.length > 0) {
        const noStyles = addedRows
          .map((row: any) => row.no_style)
          .filter((val: any) => val !== undefined && val !== null);

        const joinedNoStyles = noStyles.join(",");

        const sqlQuery2 = {
          sql: "select * from mas_styleh_tex where cd_firm = :cd_firm and no_style in (select dc from fn_mas_split(:no_style,','))",
          cd_firm: userInfo.cd_firm,
          no_style: joinedNoStyles,
        };
        const data = await getTablebySql(sqlQuery2);

        if (data.length > 0) {
          await showDialog(
            "error",
            `[스타일코드: ${data[0].no_style}] 해당 스타일 코드는 이미 등록되어 있습니다. 다른 코드로 변경해 주세요.`
          );
          return false;
        }
      }

      const modifiedRows = [
        ...(gridGetChanges.insert || []),
        ...(gridGetChanges.update || []),
      ];
      const deletedRows = gridGetChanges?.delete || [];

      let styleL: any[] = [];
      let masItemD: any[] = [];
      let masItemU: any[] = [];

      if (modifiedRows.length > 0) {
        const noStyles = modifiedRows
          .map((row: any) => row.no_style)
          .filter((val: any) => val !== undefined && val !== null);

        const joinedNoStyles = noStyles.join(",");

        const sqlQuery2 = {
          sql: "select i.*, s.dc AS no_style from mas_item i join fn_mas_split(:no_style, ',') s on i.dc_dummy01 = s.dc where i.cd_firm = :cd_firm",
          cd_firm: userInfo.cd_firm,
          no_style: joinedNoStyles,
        };

        const itemList = await getTablebySql(sqlQuery2);

        const styleNameMap = new Map(
          modifiedRows.map((row: any) => [row.no_style, row.nm_style])
        );
        const updatedItems = itemList.map((item: any) => {
          const prefix = item.no_style;
          const cdItem = item.cd_item;

          const remaining = cdItem.slice(prefix.length);

          const newNmStyle = styleNameMap.get(prefix);
          if (!newNmStyle) return item;

          return {
            ...item,
            nm_item: newNmStyle + remaining,
          };
        });
        masItemU = updatedItems;
      }

      if (deletedRows.length > 0) {
        const noStyles = deletedRows
          .map((row: any) => row.no_style)
          .filter((val: any) => val !== undefined && val !== null);

        const joinedNoStyles = noStyles.join(",");

        const sqlQuery = {
          sql: "select * from mas_stylel_tex where cd_firm = :cd_firm and no_style in (select dc from fn_mas_split(:no_style,','))",
          cd_firm: userInfo.cd_firm,
          no_style: joinedNoStyles,
        };
        const styleLData = await getTablebySql(sqlQuery);

        styleL = styleLData;

        const sqlQuery2 = {
          sql: "select * from mas_item where cd_firm = :cd_firm and dc_dummy01 in (select dc from fn_mas_split(:no_style,','))",
          cd_firm: userInfo.cd_firm,
          no_style: joinedNoStyles,
        };
        const item = await getTablebySql(sqlQuery2);
        masItemD = item;
      }

      const saveData = {
        mas_styleh_tex: gridGetChanges,
        mas_stylel_tex: {
          delete: styleL,
        },
        mas_item: {
          update: masItemU,
          delete: masItemD,
        },
      };
      const ok = await fnSave("SN_MAS_BAS_REG_001_TEX_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id === "btnApp") {
      if (gridRefs.current["grid1"].current.getDisplayedRowCount() === 0)
        return;
      const selectedRow = gridRefs.current["grid1"].current.getSelectedRows();
      if (!selectedRow) return;

      if (selectedRow[0].state === "insert") {
        await showDialog("warning", "저장한 뒤에 사용하실 수 있습니다.");
        return;
      }

      const component = (
        <SN_MAS_BAS_REG_001_TEX_POP_001 selectedData={selectedRow[0]} />
      );
      const result = await showPopupModal({
        title: "규격등록",
        children: component,
        validation: true,
        okayBtn: false,
        closeBtn: false,
      });

      if (!result || result === "close") return;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "aButton_app") {
      const component = <SN_MAS_BAS_REG_001_TEX_POP_002 />;
      const result = await showPopupModal({
        title: "품목환경설정",
        children: component,
        okayBtnText: "저장",
      });
      if (!result || result === "close") return;
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {},
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        />
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnGrid
          ref={gridRefs.current["grid1"]}
          columnConfig={grids.grid1.columns}
          rowData={grids.grid1.dataSource}
          etcProps={{
            rowSelection: "single",
            title: "스타일정보",
          }}
          events={grids.grid1.events}
          header={
            <div>
              <SnButton
                text={"규격등록"}
                onClick={() => {
                  onGrid1BtnsClick("btnApp");
                }}
              />
              <SnButton
                text={"품목환경설정"}
                onClick={() => {
                  onGrid2BtnsClick("aButton_app");
                }}
              />
            </div>
          }
        />
      </div>
      <div style={{ display: "none" }}>
        <SnGrid
          ref={sheetref1} // Grid Name
          columnConfig={grids.sheet1.columns} // 컬럼 설정 전달
          rowData={grids.sheet1.dataSource} // Datasource
        />
      </div>
    </div>
  );
});

export default SN_MAS_BAS_REG_001_TEX;
